import React from 'react';
import SeoLandingPage from './SeoLandingPage';

/**
 * Persona landing pages — one component handles all four (agents, brokers,
 * tenants, sellers). Each gets its own URL but uses tailored copy.
 */

const PERSONA = {
  agents: {
    pretitle: 'For Estate Agents',
    title: 'List unlimited properties',
    titleAccent: 'without paying Rightmove fees',
    description: 'Bronze £19 / Silver £39 / Gold £69 (90-day subscription). AI listing tools, ID-verified leads, agent-to-agent rewards, and priority placement for verified agents. Try it free.',
    ctaLabel: 'See pricing',
    ctaTo: '/for-agents',
    secondaryCtaLabel: 'Sign up free',
    secondaryCtaTo: '/register?role=agent',
    bullets: [
      { title: 'Unlimited listings', body: 'Gold tier removes listing caps entirely — list as many properties as you handle.' },
      { title: 'AI productivity', body: 'AI floor plan redraws, listing copy, valuations, video scripts. All Shopperden-watermarked.' },
      { title: 'Buyer-of-the-week alerts', body: 'High-intent buyers get matched to your live listings automatically — not just dropped in a search box.' },
    ],
    faq: [
      { q: 'How is this cheaper than Rightmove?', a: 'Rightmove averages £700+/month per branch in the UK. Shopperden Gold is £69 for 90 days — under £25/month — with unlimited listings.' },
      { q: 'Are leads verified?', a: 'Yes. Buyers and tenants pass ID verification before contacting agents — drastically reducing time-wasters.' },
    ],
  },
  brokers: {
    pretitle: 'For Brokers & Brokerages',
    title: 'Run your whole brokerage',
    titleAccent: 'on Shopperden',
    description: 'Multi-agent dashboard, leaderboard for healthy team competition, white-label profile pages, and shared lead pool. Brokers across the UK, India and Canada are migrating off legacy CRMs.',
    ctaLabel: 'Talk to sales',
    ctaTo: '/contact',
    secondaryCtaLabel: 'Compare us',
    secondaryCtaTo: '/for-agents',
    bullets: [
      { title: 'Team dashboards', body: 'See every agent\'s pipeline, deals in progress, and conversion rates in one view.' },
      { title: 'White-label brand', body: 'Your brokerage logo on profile pages and lead-capture forms.' },
      { title: 'Shared lead pool', body: 'Routes by location and specialism. No more "who got that enquiry first" arguments.' },
    ],
    faq: [
      { q: 'Can I move my existing listings?', a: 'Yes. We offer a free CSV import for any brokerage with 50+ listings. Talk to our team.' },
      { q: 'Is there a per-agent fee?', a: 'No. Brokers pay a single subscription with the agent allotment — not per-seat.' },
    ],
  },
  tenants: {
    pretitle: 'For Tenants',
    title: 'Find your next home',
    titleAccent: 'without agent gatekeepers',
    description: 'Browse direct-from-landlord rentals across the UK, India and Canada. 360° tours, verified ads, AI rent valuation, and a free tenancy template — all in one place.',
    ctaLabel: 'Browse rentals',
    ctaTo: '/properties',
    secondaryCtaLabel: 'Tenant guide',
    secondaryCtaTo: '/tenant-guide',
    bullets: [
      { title: 'Verified landlords', body: 'Only ID-verified landlords and agents can advertise — no scam listings.' },
      { title: 'Free tenancy template', body: 'AI-drafted tenancy agreement, country-correct (AST UK / Model Tenancy Act IN / provincial CA).' },
      { title: 'Saved searches', body: 'Tell us your criteria once. We\'ll alert you the moment a matching home goes live.' },
    ],
    faq: [
      { q: 'Are there hidden fees?', a: 'No tenant fees. UK tenants pay only their statutory deposit. India/Canada follow local norms.' },
      { q: 'Is the tenancy template legally binding?', a: 'It\'s an AI-generated starting draft, clearly watermarked. Have it reviewed by a solicitor/advocate before signing.' },
    ],
  },
  sellers: {
    pretitle: 'For Sellers',
    title: 'Sell your home',
    titleAccent: 'and keep the commission',
    description: 'List with 360° tours, AI valuations and direct buyer enquiries. No estate agent commissions. Featured across the UK, India and Canada from one listing.',
    ctaLabel: 'List free',
    ctaTo: '/register?role=seller',
    secondaryCtaLabel: 'Selling guide',
    secondaryCtaTo: '/selling-guide',
    bullets: [
      { title: 'Keep your equity', body: 'No 1.5% sale commission means you keep tens of thousands of pounds / lakhs / dollars more.' },
      { title: 'AI listing power', body: 'AI-generated description, 30-sec video script, floor plan redraw — all done in minutes.' },
      { title: 'Verified buyers', body: 'Buyers pass ID verification before they can message — fewer time-wasters, more genuine interest.' },
    ],
    faq: [
      { q: 'How much does it cost to list?', a: 'Free during our trial. Buyers pay a small one-off introduction fee — sellers keep 100% of sale proceeds.' },
      { q: 'How quickly will I get enquiries?', a: 'Most direct sellers get their first enquiry within 72 hours of going live, especially with a 360° tour and AI-polished description.' },
    ],
  },
};

const PersonaLandingPage = ({ persona }) => {
  const data = PERSONA[persona];
  if (!data) return null;
  return (
    <SeoLandingPage
      testIdPrefix={`persona-${persona}`}
      {...data}
      seoTitle={`${data.pretitle} — Shopperden | UK · India · Canada`}
      seoDescription={data.description.slice(0, 160)}
    />
  );
};

export default PersonaLandingPage;
